import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wallet, TrendingUp, Banknote, ArrowDownToLine, Clock, Loader2, Info,
  IndianRupee, Split, ExternalLink, CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useIdempotentPost } from "@/hooks/useIdempotentPost";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// /merchant/payouts — settlement dashboard for merchants who accept NFC pay
// or split-pay. Shows lifetime + this-month totals, incoming settlements
// (payments in) and outgoing withdrawals (bank transfers).
// -----------------------------------------------------------------------------
const inr = paise => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function MerchantPayouts() {
  const { user } = useAuth();
  const [summary, setSummary]     = useState(null);
  const [settlements, setSettles] = useState([]);
  const [withdrawals, setWithd]   = useState([]);
  const [amount, setAmount]       = useState("");
  const [bankRef, setBankRef]     = useState("");
  const [busy, setBusy]           = useState(false);

  useSeo({
    title: "Merchant payouts · settlements + withdrawals · find2near",
    description: "See every NFC and split-pay settlement in one place, and withdraw to your bank in one tap.",
    canonical: "/merchant/payouts",
  });

  const loadAll = async () => {
    try {
      const [s, se, w] = await Promise.all([
        api.get("/merchant/payouts/summary"),
        api.get("/merchant/payouts/settlements"),
        api.get("/merchant/payouts/withdrawals"),
      ]);
      setSummary(s.data); setSettles(se.data); setWithd(w.data);
    } catch { toast.error("Couldn't load payouts"); }
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const postOnce = useIdempotentPost("wd");
  const withdraw = async () => {
    const paise = Math.round(Number(amount) * 100);
    if (!paise || paise < 100) { toast.error("Minimum withdrawal is ₹1"); return; }
    setBusy(true);
    try {
      await postOnce("/merchant/payouts/withdraw", { amount_paise: paise, bank_ref: bankRef.trim() || undefined });
      toast.success("Withdrawal queued");
      setAmount(""); setBankRef("");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdrawal failed"); }
    finally { setBusy(false); }
  };

  if (!user) return (
    <div className="max-w-md mx-auto text-center py-24 px-6">
      <Wallet className="mx-auto text-emerald-500" size={44}/>
      <h2 className="heading text-2xl font-black mt-4">Sign in to open payouts</h2>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="merchant-payouts">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Banknote size={12} className="text-emerald-600"/> Merchant · Payouts</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every rupee. <span className="text-emerald-700">In one place.</span></h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">Settlements from NFC pay + split-pay, plus one-tap withdrawal to bank.</p>
      </div>

      {!summary ? (
        <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-stone-400"/></div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid sm:grid-cols-4 gap-2" data-testid="payouts-kpis">
            <KPI label="Wallet balance"  amount={summary.wallet_balance_paise}   sub="available now"      Icon={Wallet}     grad="from-emerald-600 to-teal-800"/>
            <KPI label="This month"      amount={summary.this_month.gross_paise} sub={`${summary.this_month.pay_count} pays · ${summary.this_month.split_count} splits`} Icon={TrendingUp} grad="from-blue-600 to-indigo-800"/>
            <KPI label="Lifetime gross"  amount={summary.lifetime.gross_paise}   sub={`${summary.lifetime.pay_count + summary.lifetime.split_count} txns`} Icon={Banknote}   grad="from-amber-500 to-orange-700"/>
            <KPI label="Pending w/d"     amount={summary.pending_withdrawals_paise} sub={`${summary.pending_withdrawals_count} queued`}           Icon={Clock}      grad="from-stone-700 to-stone-900"/>
          </div>

          {/* Withdraw form */}
          <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-2" data-testid="payouts-withdraw">
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-500">Withdraw to bank</div>
            <div className="flex flex-wrap gap-2">
              <Input inputMode="decimal" placeholder="Amount ₹" value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-full text-sm w-36" data-testid="payouts-amount"/>
              <Input placeholder="Bank label (e.g. HDFC ****1234)" value={bankRef} onChange={e => setBankRef(e.target.value)} className="rounded-full text-sm flex-1 min-w-[180px]" data-testid="payouts-bankref"/>
              <Button onClick={withdraw} disabled={busy || !amount} className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9" data-testid="payouts-withdraw-btn">
                {busy ? <Loader2 size={12} className="animate-spin mr-1"/> : <ArrowDownToLine size={12} className="mr-1"/>} Queue withdrawal
              </Button>
            </div>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl p-2 flex items-start gap-2">
              <Info size={11} className="mt-0.5 shrink-0"/>
              <span>Bank rails go live once Razorpay/RazorpayX credentials are added. Withdrawals stay queued as status <b>pending</b> until then — your wallet is debited immediately, so the same money can't be double-withdrawn.</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <List title={`Incoming settlements · ${settlements.length}`} data-testid="payouts-settlements">
              {settlements.length === 0
                ? <Empty text="No incoming payments yet."/>
                : settlements.map(s => (
                  <li key={s.id} className="p-3 flex items-center gap-2 text-sm" data-testid={`settlement-${s.id}`}>
                    {s.kind === "nfc_pay" ? <IndianRupee size={13} className="text-emerald-700"/> : <Split size={13} className="text-orange-700"/>}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{inr(s.amount_paise)}</div>
                      <div className="text-[11px] text-stone-500 truncate">{s.kind.replace("_", " ")}{s.note && ` · ${s.note}`}</div>
                    </div>
                    <div className="text-[10px] font-mono text-stone-400 shrink-0">{(s.created_at || "").slice(0, 10)}</div>
                  </li>
                ))
              }
            </List>

            <List title={`Withdrawals · ${withdrawals.length}`} data-testid="payouts-withdrawals-list">
              {withdrawals.length === 0
                ? <Empty text="No withdrawals yet."/>
                : withdrawals.map(w => (
                  <li key={w.withdraw_id} className="p-3 flex items-center gap-2 text-sm" data-testid={`withdrawal-${w.withdraw_id}`}>
                    <ArrowDownToLine size={13} className="text-blue-700"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold">{inr(w.amount_paise)}</div>
                      <div className="text-[11px] text-stone-500 truncate">{w.bank_ref || "no bank tag"}</div>
                    </div>
                    <span className={`text-[9px] font-black uppercase rounded-full px-2 py-0.5 ${w.status === "settled" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`withdrawal-status-${w.withdraw_id}`}>
                      {w.status}
                    </span>
                    <div className="text-[10px] font-mono text-stone-400 shrink-0">{(w.created_at || "").slice(0, 10)}</div>
                  </li>
                ))
              }
            </List>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, amount, sub, Icon, grad }) {
  return (
    <div className={`rounded-3xl bg-gradient-to-br ${grad} text-white p-3`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-2 opacity-80"><Icon size={13}/><span className="text-[10px] uppercase tracking-widest font-black">{label}</span></div>
      <div className="text-2xl font-black mt-1">{`₹${(Number(amount || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}</div>
      <div className="text-[11px] opacity-80">{sub}</div>
    </div>
  );
}

function List({ title, children, ...rest }) {
  return (
    <section className="rounded-3xl bg-white border border-stone-200 overflow-hidden" {...rest}>
      <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">{title}</div>
      <ul className="divide-y divide-stone-100 max-h-96 overflow-y-auto">{children}</ul>
    </section>
  );
}

function Empty({ text }) { return <div className="p-6 text-center text-sm text-stone-500">{text}</div>; }
